import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { SortableElement, SortableContainer } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';

import Swal from 'sweetalert2';
import Button from 'react-bootstrap/Button';

import { selectStatusByName, selectSectionAddClicksByName } from '@redux/selectors/FormSelector';
import { selectSectionsPerRepeatableSection } from '@redux/selectors/ListingSelector';

import ParcelCard from './ParcelCard';

const MAX = 2;
const whiteList = [
  'TitleType',
  'LandWestMeridian',
  'LandTaxAmount',
  'LandTownship',
  'LandSection',
  'LandRange',
  'LandQuarter',
  'LandLincNumber',
  'LandAcres',
];
const SortableCard = SortableElement(props => (
  <ParcelCard {...props} />
));

const Grid = SortableContainer(({
  fileList,
  fieldsList,
  validateFigField,
  something,
  ...otherProps
}) => (
  <div className={`grid grid-${MAX}-col`}>
    {fileList.map(([k, file], index) => (
      (!file.IsDeleted)
      && (
        <SortableCard
          key={k} // consumed by react-sortable-hoc
          index={index} // consumed by react-sortable-hoc
          sortIndex={index}
          file={file}
          validateFigField={validateFigField}
          fieldsList={fieldsList}
          {...otherProps}
        />
      )
    ))}
  </div>
));

const RepeatableParcels = ({ fields, validateFigField, parcelsPayload }) => {
  const { values, setValues, setFieldValue } = useFormikContext();
  const statusParcels = useSelector(state => selectStatusByName(state, 'Parcels'));
  const addClickCounter = useSelector(state => selectSectionAddClicksByName(state, 'Parcels'));
  const maxCount = useMemo(() => ((fields[0].repeatableMaxCount) ? fields[0].repeatableMaxCount : 25), [fields]);
  const sectionsPerRepeatableSection = useSelector(selectSectionsPerRepeatableSection);
  const parcelsCount = sectionsPerRepeatableSection.Parcels.length;

  const fileList = useMemo(() => {
    const array = [];
    if (values.Parcels) {
      Object.entries(values.Parcels).forEach(([k, file]) => {
        if (!file.IsDeleted) {
          array.push([k, file]);
        }
      });
    }
    return array;
  }, [values?.Parcels]);

  const fieldsList = useMemo(() => {
    const array = [];
    fields.forEach(field => {
      if (whiteList.includes(field.FieldName)) {
        array.push(field);
      }
    });
    return array;
  }, [fields]);

  const buildFormikValues = () => {
    const newValues = {};
    let index = 0;
    parcelsPayload.sort((a, b) => a.SortOrder - b.SortOrder).forEach(entry => {
      const uid = uuidv4(); // keep this an unique string that cannot be converted to a number (prevents keys reordering)
      newValues[uid] = {
        uid,
        UniqueID: entry.UniqueID,
        TitleType: entry.TitleType,
        LandWestMeridian: entry.LandWestMeridian,
        LandTaxAmount: entry.LandTaxAmount,
        LandTownship: entry.LandTownship,
        LandSection: entry.LandSection,
        LandRange: entry.LandRange,
        LandQuarter: entry.LandQuarter,
        LandLincNumber: entry.LandLincNumber,
        LandAcres: entry.LandAcres,
        SortOrder: entry.SortOrder,
        IsDeleted: entry.IsDeleted,
        IsLive: entry.IsLive,
      };
      // ensure correct sort order
      if (!entry.IsDeleted) {
        newValues[uid].SortOrder = index + 1;
        index += 1;
      }
    });
    return newValues;
  };

  useEffect(() => {
    if (parcelsPayload?.length) {
      const newValues = buildFormikValues();
      setFieldValue('Parcels', {
        ...values.Parcels,
        ...newValues,
      });
    }
  }, [parcelsPayload]);

  const displayMaxCountExceedMessage = () => Swal.fire({
    position: 'center',
    title: 'Parcels Exceeded',
    html: `The maximum number of parcels must not exceed <strong class="text-danger">${maxCount}</strong>.`,
    icon: 'error',
    iconColor: 'var(--theme-danger)',
    showConfirmButton: false,
    timer: 5000,
  });

  const addParcel = () => {
    if (parcelsCount + 1 > maxCount) {
      displayMaxCountExceedMessage();
      return;
    }

    const uid = uuidv4();
    setValues({
      ...values,
      Parcels: {
        ...values.Parcels,
        [uid]: {
          uid,
          TitleType: '',
          LandWestMeridian: '',
          LandTaxAmount: '',
          LandTownship: '',
          LandSection: '',
          LandRange: '',
          LandQuarter: '',
          LandLincNumber: '',
          LandAcres: '',
          SortOrder: fileList.length + 1, // fileList excludes deleted entries
          IsDeleted: false,
          IsLive: false,
        },
      },
    });
  };

  // after sort ends derive new order, then set formik state
  const onSortEnd = ({ oldIndex, newIndex }) => {
    const sortedArray = arrayMoveImmutable(fileList, oldIndex, newIndex);
    let newValues = {};
    sortedArray.forEach((file, index) => {
      newValues = {
        ...newValues,
        [file[0]]: {
          ...file[1],
          SortOrder: index + 1,
        },
      };
    });
    // preserve deleted entries - excluded from fileList
    Object.entries(values.Parcels).forEach(file => {
      if (file[1].IsDeleted) {
        newValues = {
          ...newValues,
          [file[0]]: {
            ...file[1],
          },
        };
      }
    });
    setValues({
      ...values,
      Parcels: { ...newValues },
    });
  };

  useEffect(() => {
    if (addClickCounter) {
      addParcel();
    }
  }, [addClickCounter]);

  return (
    <>
      { statusParcels?.errorMessage && <div className="invalid-feedback">{statusParcels.errorMessage}</div> }
      <Button
        name="btnAddParcel"
        className="float-right"
        onClick={addParcel}
        size="sm"
      >
        <i className="fas fa-plus mr-2" />
        Add Parcel
      </Button>
      <br className="clearfix" />
      <Grid
        axis="xy"
        distance={20} // set distance to prevent click events from being swallowed
        fileList={fileList}
        fieldsList={fieldsList}
        validateFigField={validateFigField}
        onSortEnd={onSortEnd}
        useDragHandle
      />
      <br className="clearfix" />
      {(parcelsCount > MAX) && (
        <>
          <Button
            name="btnAddParcel"
            className="float-right"
            onClick={addParcel}
            size="sm"
          >
            <i className="fas fa-plus mr-2" />
            Add Parcel
          </Button>
          <br className="clearfix" />
        </>
      )}
    </>
  );
};

RepeatableParcels.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.shape()),
  validateFigField: PropTypes.func,
  parcelsPayload: PropTypes.arrayOf(PropTypes.shape()),
};

export default RepeatableParcels;
